import { useQuery } from "@apollo/client/react";
import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { faLayerGroup } from "@fortawesome/pro-solid-svg-icons";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useTemplates } from "./use-templates";
import { SCRIBENOTE_DEFAULTS } from "../components/common/record/constants";
import { toTemplateGroupValue } from "../components/dashboard/utils";
import { GET_TEMPLATE_GROUPS } from "../graphql/queries/TemplateGroups";

/**
 * Shared hook that builds grouped dropdown options for template selection.
 * Used by both AutoTemplatesModal and RecordingJobTypeSelection.
 */
export function useTemplateSelection({
  hideScribenoteDefaults = false,
  hideTemplateGroups = false,
} = {}) {
  const {
    allTemplates,
    ownedTemplates,
    sharedTemplates,
    teamAndOrgDefaultTemplates,
  } = useTemplates();

  const { releaseGlobalGroupedNotes } = useFlags();

  const { data: templateGroupsData } = useQuery(GET_TEMPLATE_GROUPS, {
    skip: !releaseGlobalGroupedNotes,
    fetchPolicy: "cache-and-network",
  });

  const addIcon = (t) => ({
    ...t,
    icon: t.templateType === "SOAP" ? faSoapNote : faFile,
  });

  // Favorites first — these get excluded from other sections
  const favoriteTemplates = allTemplates
    .filter((t) => t.isFavourite)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({ ...addIcon(t), group: "favorites" }));

  const favoriteUuids = new Set(favoriteTemplates.map((t) => t.uuid));

  // Owned templates split by type, excluding favorites
  const ownedSoaps = ownedTemplates
    .filter(
      (t) => t.templateType === "SOAP" && !favoriteUuids.has(t.uuid),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({ ...addIcon(t), group: "owned-soap" }));

  const ownedFreeform = ownedTemplates
    .filter(
      (t) =>
        t.templateType === "CUSTOM" && !favoriteUuids.has(t.uuid),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({ ...addIcon(t), group: "owned-freeform" }));

  // Team defaults, deduped and excluding favorites
  const teamTemplates = (() => {
    const templateMap = new Map();
    teamAndOrgDefaultTemplates.forEach((template) => {
      templateMap.set(template.uuid, template);
    });
    return Array.from(templateMap.values())
      .filter((t) => !favoriteUuids.has(t.uuid))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({ ...addIcon(t), group: "team" }));
  })();

  const teamUuids = new Set(teamTemplates.map((t) => t.uuid));

  // Shared templates, excluding favorites and team defaults
  const shared = sharedTemplates
    .filter(
      (t) => !favoriteUuids.has(t.uuid) && !teamUuids.has(t.uuid),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({ ...addIcon(t), group: "shared" }));

  // Template groups
  const templateGroups = (
    templateGroupsData?.templateGroups || []
  ).map((g) => {
    const value = toTemplateGroupValue(g.uuid);
    return {
      ...g,
      // Override uuid so getOptionValue returns the prefixed value,
      // matching what gets stored in selectedJobType.value
      uuid: value,
      value,
      name: g.name,
      icon: faLayerGroup,
      isTemplateGroup: true,
      group: "template-groups",
      description: (g.templates || [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((t) => t.autoTemplate?.name)
        .filter(Boolean)
        .join("\n"),
    };
  });

  // Build grouped options with section headers
  const options = [];

  if (favoriteTemplates.length > 0) {
    options.push({
      isHeader: true,
      id: "favorites-header",
      title: "Favorites",
      icon: faStar,
    });
    options.push(...favoriteTemplates);
  }

  if (templateGroups.length > 0 && !hideTemplateGroups) {
    options.push({
      isHeader: true,
      id: "template-groups-header",
      title: "Template Groups",
    });
    options.push(...templateGroups);
  }

  if (ownedSoaps.length > 0) {
    options.push({
      isHeader: true,
      id: "owned-soap-header",
      title: "My SOAPs",
    });
    options.push(...ownedSoaps);
  }

  if (ownedFreeform.length > 0) {
    options.push({
      isHeader: true,
      id: "owned-freeform-header",
      title: "My Freeform",
    });
    options.push(...ownedFreeform);
  }

  if (teamTemplates.length > 0) {
    options.push({
      isHeader: true,
      id: "team-defaults-header",
      title: "Team Defaults",
    });
    options.push(...teamTemplates);
  }

  if (shared.length > 0) {
    options.push({
      isHeader: true,
      id: "shared-header",
      title: "Shared with you",
    });
    options.push(...shared);
  }

  if (!hideScribenoteDefaults) {
    options.push({
      isHeader: true,
      id: "defaults-header",
      title: "Scribenote Defaults",
    });
    options.push(...SCRIBENOTE_DEFAULTS);
  }

  const allUserTemplates = [
    ...ownedTemplates,
    ...teamAndOrgDefaultTemplates,
    ...sharedTemplates,
  ];

  return { options, allUserTemplates };
}
